import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Form,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Button,
  Card,
  CardBody,
  CardHeader,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { cpfRequired, cepRequired, phoneRequired, emailValidator } from '../../../utils/yupValidators';

interface Phase1PersonalInfoFormProps {
  onSubmit: (values: any) => Promise<void>;
  initialValues: any;
  isSubmitting: boolean;
}

interface AddressFormValues {
  cep: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
}

interface FormValues {
  name: string;
  cpf: string;
  phone: string;
  birthDate: string;
  rg: string;
  email: string;
  professionalEntity: string;
  address: AddressFormValues;
}

const Phase1PersonalInfoForm: React.FC<Phase1PersonalInfoFormProps> = ({
  onSubmit,
  initialValues,
  isSubmitting,
}) => {
  const { t } = useTranslation();
  const [loadingCep, setLoadingCep] = useState(false);

  const validationSchema = Yup.object({
    name: Yup.string().required(t('Nome é obrigatório')),
    cpf: cpfRequired,
    phone: phoneRequired,
    birthDate: Yup.date()
      .required(t('Data de nascimento é obrigatória'))
      .max(new Date(), t('Data de nascimento não pode ser futura')),
    rg: Yup.string().nullable(),
    email: emailValidator.nullable(),
    professionalEntity: Yup.string().nullable(),
    address: Yup.object({
      cep: cepRequired,
      street: Yup.string().required(t('Logradouro é obrigatório')),
      number: Yup.string().required(t('Número é obrigatório')),
      complement: Yup.string().nullable(),
      neighborhood: Yup.string().required(t('Bairro é obrigatório')),
      city: Yup.string().required(t('Cidade é obrigatória')),
      state: Yup.string()
        .required(t('Estado é obrigatório'))
        .length(2, t('Estado deve ter 2 caracteres')),
    }).required(),
  });

  const formik = useFormik<FormValues>({
    initialValues: initialValues || {
      name: '',
      cpf: '',
      phone: '',
      birthDate: '',
      rg: '',
      email: '',
      professionalEntity: '',
      address: {
        cep: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
      },
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      await onSubmit(values);
    },
  });

  // Handle form submission with validation check
  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Trigger validation on all fields
    const errors = await formik.validateForm();
    formik.setTouched({
      name: true,
      cpf: true,
      phone: true,
      birthDate: true,
      rg: true,
      email: true,
      professionalEntity: true,
      address: {
        cep: true,
        street: true,
        number: true,
        complement: true,
        neighborhood: true,
        city: true,
        state: true,
      },
    });

    // Check if there are validation errors
    if (Object.keys(errors).length > 0) {
      toast.error(t('Por favor, preencha todos os campos obrigatórios corretamente.'));
      return;
    }

    // If validation passes, submit the form
    formik.handleSubmit(e);
  };

  const handleCepBlur = async () => {
    const cep = formik.values.address.cep.replace(/\D/g, '');
    if (cep.length === 8) {
      setLoadingCep(true);
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();

        if (!data.erro) {
          formik.setFieldValue('address.street', data.logradouro);
          formik.setFieldValue('address.neighborhood', data.bairro);
          formik.setFieldValue('address.city', data.localidade);
          formik.setFieldValue('address.state', data.uf);
        }
      } catch (error) {
        console.error('Error fetching CEP:', error);
      } finally {
        setLoadingCep(false);
      }
    }
  };

  return (
    <Form onSubmit={handleFormSubmit}>
      <Card className="mb-3">
        <CardHeader>
          <h5 className="mb-0">{t('Informações Pessoais')}</h5>
        </CardHeader>
        <CardBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="name">
                  {t('Nome Completo')} <span className="text-danger">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder={t('Digite o nome completo')}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.name && !!formik.errors.name}
                />
                <FormFeedback>{formik.errors.name}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md={3}>
              <FormGroup>
                <Label for="cpf">
                  {t('CPF')} <span className="text-danger">*</span>
                </Label>
                <Input
                  id="cpf"
                  name="cpf"
                  type="text"
                  placeholder="000.000.000-00"
                  value={formik.values.cpf}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.cpf && !!formik.errors.cpf}
                />
                <FormFeedback>{formik.errors.cpf}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md={3}>
              <FormGroup>
                <Label for="rg">{t('RG')}</Label>
                <Input
                  id="rg"
                  name="rg"
                  type="text"
                  placeholder={t('Digite o RG')}
                  value={formik.values.rg}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.rg && !!formik.errors.rg}
                />
                <FormFeedback>{formik.errors.rg}</FormFeedback>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <FormGroup>
                <Label for="birthDate">
                  {t('Data de Nascimento')} <span className="text-danger">*</span>
                </Label>
                <Input
                  id="birthDate"
                  name="birthDate"
                  type="date"
                  value={formik.values.birthDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.birthDate && !!formik.errors.birthDate}
                />
                <FormFeedback>{formik.errors.birthDate}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label for="phone">
                  {t('Telefone')} <span className="text-danger">*</span>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="(00) 00000-0000"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.phone && !!formik.errors.phone}
                />
                <FormFeedback>{formik.errors.phone}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label for="email">{t('E-mail')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder={t('Digite o e-mail')}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.email && !!formik.errors.email}
                />
                <FormFeedback>{formik.errors.email}</FormFeedback>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <FormGroup>
                <Label for="professionalEntity">{t('Entidade de Classe')}</Label>
                <Input
                  id="professionalEntity"
                  name="professionalEntity"
                  type="text"
                  placeholder={t('Ex: CRC, OAB, CRM, etc.')}
                  value={formik.values.professionalEntity}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.professionalEntity && !!formik.errors.professionalEntity}
                />
                <FormFeedback>{formik.errors.professionalEntity}</FormFeedback>
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card className="mb-3">
        <CardHeader>
          <h5 className="mb-0">{t('Endereço')}</h5>
        </CardHeader>
        <CardBody>
          <Row>
            <Col md={3}>
              <FormGroup>
                <Label for="address.cep">
                  {t('CEP')} <span className="text-danger">*</span>
                </Label>
                <Input
                  id="address.cep"
                  name="address.cep"
                  type="text"
                  placeholder="00000-000"
                  value={formik.values.address.cep}
                  onChange={formik.handleChange}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    handleCepBlur();
                  }}
                  invalid={formik.touched.address?.cep && !!formik.errors.address?.cep}
                  disabled={loadingCep}
                />
                <FormFeedback>{formik.errors.address?.cep}</FormFeedback>
                {loadingCep && <small className="text-muted">{t('Buscando CEP...')}</small>}
              </FormGroup>
            </Col>

            <Col md={7}>
              <FormGroup>
                <Label for="address.street">
                  {t('Logradouro')} <span className="text-danger">*</span>
                </Label>
                <Input
                  id="address.street"
                  name="address.street"
                  type="text"
                  placeholder={t('Rua, Avenida, etc.')}
                  value={formik.values.address.street}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.address?.street && !!formik.errors.address?.street}
                />
                <FormFeedback>{formik.errors.address?.street}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md={2}>
              <FormGroup>
                <Label for="address.number">
                  {t('Número')} <span className="text-danger">*</span>
                </Label>
                <Input
                  id="address.number"
                  name="address.number"
                  type="text"
                  placeholder="000"
                  value={formik.values.address.number}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.address?.number && !!formik.errors.address?.number}
                />
                <FormFeedback>{formik.errors.address?.number}</FormFeedback>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <FormGroup>
                <Label for="address.complement">{t('Complemento')}</Label>
                <Input
                  id="address.complement"
                  name="address.complement"
                  type="text"
                  placeholder={t('Apto, Bloco, etc.')}
                  value={formik.values.address.complement}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.address?.complement && !!formik.errors.address?.complement}
                />
                <FormFeedback>{formik.errors.address?.complement}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label for="address.neighborhood">
                  {t('Bairro')} <span className="text-danger">*</span>
                </Label>
                <Input
                  id="address.neighborhood"
                  name="address.neighborhood"
                  type="text"
                  placeholder={t('Digite o bairro')}
                  value={formik.values.address.neighborhood}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.address?.neighborhood && !!formik.errors.address?.neighborhood}
                />
                <FormFeedback>{formik.errors.address?.neighborhood}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md={3}>
              <FormGroup>
                <Label for="address.city">
                  {t('Cidade')} <span className="text-danger">*</span>
                </Label>
                <Input
                  id="address.city"
                  name="address.city"
                  type="text"
                  placeholder={t('Digite a cidade')}
                  value={formik.values.address.city}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.address?.city && !!formik.errors.address?.city}
                />
                <FormFeedback>{formik.errors.address?.city}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md={1}>
              <FormGroup>
                <Label for="address.state">
                  {t('UF')} <span className="text-danger">*</span>
                </Label>
                <Input
                  id="address.state"
                  name="address.state"
                  type="text"
                  placeholder="SP"
                  maxLength={2}
                  value={formik.values.address.state}
                  onChange={(e) => formik.setFieldValue('address.state', e.target.value.toUpperCase())}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.address?.state && !!formik.errors.address?.state}
                />
                <FormFeedback>{formik.errors.address?.state}</FormFeedback>
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <div className="d-flex justify-content-end gap-2 mt-4">
        <Button
          type="submit"
          color="primary"
          disabled={isSubmitting || !formik.isValid}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {t('Salvando...')}
            </>
          ) : (
            <>
              {t('Próximo')}
              <i className="ri-arrow-right-line ms-1"></i>
            </>
          )}
        </Button>
      </div>
    </Form>
  );
};

export default Phase1PersonalInfoForm;
